import type { ReactNode } from 'react';
import { cn } from '../cn';

export interface PageHeaderProps {
  title: ReactNode;
  /** Mono 11px .06em fg4 line under the title. */
  meta?: ReactNode;
  /** Badge next to the title (live Tag). */
  badge?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Desktop only (mobile header shows the title). */
  desktopOnly?: boolean;
}

/** Section heading: h1 500 28px/1.05 −.02em (22 px mobile), mono meta, actions aligned to the baseline. */
export function PageHeader({ title, meta, badge, actions, className, desktopOnly }: PageHeaderProps) {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-4', desktopOnly && 'hidden md:flex', className)}>
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-3">
          <h1 className="m-0 text-h1m font-medium md:text-h1">{title}</h1>
          {badge}
        </div>
        {meta && <span className="font-mono text-sm font-normal leading-[1.5] tracking-meta text-fg4">{meta}</span>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Page body: 16/16/28 + 16 gap on mobile, 28/32/48 + 22 gap on desktop. */
export function Section({ children, className }: { children?: ReactNode; className?: string }) {
  return <section className={cn('flex flex-col gap-4 px-4 pb-7 pt-4 md:gap-[22px] md:px-8 md:pb-12 md:pt-7', className)}>{children}</section>;
}

/** Two-column desktop layout: main flex 1 1 560 + side 0 1 320 (wraps on narrow). */
export function Columns({ main, side, sideWidth = 320, mainMin = 560, className }: { main: ReactNode; side?: ReactNode; sideWidth?: number; mainMin?: number; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-start gap-5', className)}>
      <div className="flex min-w-0 flex-col gap-5" style={{ flex: `1 1 ${mainMin}px` }}>
        {main}
      </div>
      {side && (
        <div className="flex min-w-[280px] flex-col gap-5" style={{ flex: `0 1 ${sideWidth}px` }}>
          {side}
        </div>
      )}
    </div>
  );
}

/** Responsive card grid: auto-fit minmax(300, 1fr) with 20 px gap. */
export function CardGrid({ children, min = 300, className }: { children: ReactNode; min?: number; className?: string }) {
  return (
    <div className={cn('grid items-start gap-5', className)} style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))` }}>
      {children}
    </div>
  );
}
